import { io, type Socket } from 'socket.io-client';
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../app/auth-store.ts';
import { API_URL, refreshSession } from './api.ts';

let socket: Socket | null = null;
let socketBusiness: string | null = null;
const resyncListeners = new Set<() => void>();

/**
 * Socket.IO no namespace /business/:id, autenticado pelo access token.
 * O token é lido a cada (re)conexão: ele vence em minutos e a conexão pode cair (deploy, rede, notebook dormindo).
 * Se o servidor recusar o token, renova a sessão e reconecta. Ao reconectar, avisa para reler os dados (eventos perdidos).
 */
export function getSocket(): Socket | null {
  const { accessToken, businessId } = useAuthStore.getState();
  if (!accessToken || !businessId) return null;
  if (socket && socketBusiness === businessId) return socket;
  socket?.disconnect();
  const s = io(`${API_URL || window.location.origin}/business/${businessId}`, { auth: (cb) => cb({ token: useAuthStore.getState().accessToken ?? '' }), transports: ['websocket', 'polling'] });
  let connectedOnce = false;
  let authRetries = 0;
  s.on('connect', () => {
    authRetries = 0;
    if (connectedOnce) for (const fn of resyncListeners) fn();
    connectedOnce = true;
  });
  s.on('connect_error', async (err) => {
    // Erro do middleware (token vencido): o Socket.IO não tenta de novo sozinho.
    if (s.active || err.message !== 'unauthorized') return;
    if (authRetries >= 5) return;
    authRetries += 1;
    await new Promise((r) => setTimeout(r, 1000 * authRetries));
    if (socket === s && (await refreshSession())) s.connect();
  });
  socket = s;
  socketBusiness = businessId;
  return s;
}

/** Encerra o socket atual (logout, troca de negócio). O próximo getSocket() abre um novo no namespace certo. */
export function disconnectSocket(): void {
  socket?.disconnect();
  socket = null;
  socketBusiness = null;
}

// Logout ou troca de negócio: derruba o socket na hora para não continuar recebendo eventos do namespace antigo.
useAuthStore.subscribe((state, prev) => {
  if (!socket) return;
  if (!state.accessToken || state.businessId !== prev.businessId) disconnectSocket();
});

// Volta para a aba (ou a rede volta) com o socket desligado: reconecta na hora em vez de esperar o próximo ciclo.
if (typeof window !== 'undefined') {
  const wake = () => { if (socket && !socket.connected && document.visibilityState === 'visible') socket.connect(); };
  document.addEventListener('visibilitychange', wake);
  window.addEventListener('online', wake);
}

/** Relê todos os dados do negócio quando o socket reconecta (eventos enviados enquanto estava desconectado se perderam). */
export function useRealtimeResync() {
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const token = useAuthStore((s) => s.accessToken);
  useEffect(() => {
    if (!getSocket()) return;
    const fn = () => qc.invalidateQueries({ queryKey: [businessId] });
    resyncListeners.add(fn);
    return () => { resyncListeners.delete(fn); };
  }, [businessId, token, qc]);
}

/** Invalida queries do negócio ao receber eventos de tempo real. */
export function useRealtime(handlers: Partial<Record<'conversation.message' | 'conversation.state' | 'calendar.changed' | 'waitlist.changed' | 'whatsapp.status' | 'quota.alert', (payload: unknown) => void>>) {
  const qc = useQueryClient();
  const businessId = useAuthStore((s) => s.businessId);
  const token = useAuthStore((s) => s.accessToken);
  // Handlers ficam num ref: o socket registra uma vez, mas cada evento chama a versão mais recente (com o estado atual da tela).
  const latest = useRef(handlers);
  latest.current = handlers;
  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const entries = (Object.keys(handlers) as (keyof typeof handlers)[]).map((ev) => [ev, (p: unknown) => latest.current[ev]?.(p)] as [string, (p: unknown) => void]);
    for (const [ev, fn] of entries) s.on(ev, fn);
    const onStatus = () => qc.invalidateQueries({ queryKey: [businessId, 'whatsapp'] });
    s.on('whatsapp.status', onStatus);
    return () => { for (const [ev, fn] of entries) s.off(ev, fn); s.off('whatsapp.status', onStatus); };
  }, [businessId, token, qc]);
}
